import {
  CSSProperties,
  ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { AppEpisode } from "./api";
import { useApp } from "./context";
export type IconName =
  | "play"
  | "pause"
  | "search"
  | "arrow"
  | "plus"
  | "close"
  | "back"
  | "next"
  | "volume"
  | "muted"
  | "expand"
  | "collapse"
  | "note"
  | "check"
  | "globe"
  | "headphones"
  | "queue"
  | "user"
  | "sun"
  | "share"
  | "rewind"
  | "forward"
  | "cast"
  | "airplay";
const paths: Record<IconName, ReactNode> = {
  cast: (
    <>
      <path d="M3 7V4h18v15h-7M3 12a7 7 0 0 1 7 7M3 16a3 3 0 0 1 3 3" />
      <circle cx="3" cy="20" r="1" fill="currentColor" stroke="none" />
    </>
  ),
  airplay: (
    <>
      <path d="M5 16H3V4h18v12h-2" />
      <path d="m12 13 7 8H5Z" fill="currentColor" stroke="none" />
    </>
  ),
  play: <path d="m9 5 11 7-11 7Z" fill="currentColor" stroke="none" />,
  pause: (
    <>
      <path d="M8 5v14M16 5v14" strokeWidth="4" />
    </>
  ),
  search: (
    <>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="m16 16 5 5" />
    </>
  ),
  arrow: <path d="M4 12h16m-6-6 6 6-6 6" />,
  plus: <path d="M12 4v16M4 12h16" />,
  close: <path d="m6 6 12 12M6 18 18 6" />,
  back: <path d="M19 12H5m6-6-6 6 6 6" />,
  next: (
    <>
      <path d="m5 5 11 7-11 7Z" fill="currentColor" stroke="none" />
      <path d="M19 5v14" />
    </>
  ),
  volume: (
    <>
      <path d="m11 4-6 5H2v6h3l6 5Z" />
      <path d="M15 8q5 4 0 8M18 4q9 8 0 16" />
    </>
  ),
  muted: (
    <>
      <path d="m11 4-6 5H2v6h3l6 5Z" />
      <path d="m16 9 6 6m-6 0 6-6" />
    </>
  ),
  expand: <path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" />,
  collapse: <path d="m6 9 6 6 6-6" />,
  note: (
    <>
      <path d="M5 3h14v18l-7-4-7 4Z" />
      <path d="M8 8h8M8 12h6" />
    </>
  ),
  check: <path d="m5 12 4 4L20 5" />,
  globe: (
    <>
      <circle cx="12" cy="12" r="9" />
      <ellipse cx="12" cy="12" rx="4" ry="9" />
      <path d="M3 12h18" />
    </>
  ),
  headphones: (
    <>
      <path d="M4 15v-3a8 8 0 0 1 16 0v3" />
      <rect x="3" y="12" width="4" height="8" rx="2" />
      <rect x="17" y="12" width="4" height="8" rx="2" />
    </>
  ),
  queue: (
    <>
      <path d="M3 5h17M3 10h17M3 15h8m5 0v7m-3-3h6" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 22v-3a8 8 0 0 1 16 0v3" />
    </>
  ),
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 1v3m0 16v3M1 12h3m16 0h3M4 4l2 2m12 12 2 2M4 20l2-2M18 6l2-2" />
    </>
  ),
  share: (
    <>
      <path d="M12 16V2m-5 5 5-5 5 5M4 12v9h16v-9" />
    </>
  ),
  rewind: (
    <>
      <path d="M4 10a8 8 0 1 1 1 8M4 4v6h6" />
    </>
  ),
  forward: (
    <>
      <path d="M20 10a8 8 0 1 0-1 8m1-14v6h-6" />
    </>
  ),
};
export function Icon({ name, size = 20 }: { name: IconName; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}
export function IconButton({
  name,
  label,
  onClick,
  className = "",
  disabled = false,
}: {
  name: IconName;
  label: string;
  onClick: () => void;
  className?: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className={"icon-button " + className}
      onClick={onClick}
      title={label}
      aria-label={label}
      disabled={disabled}
    >
      <Icon name={name} />
    </button>
  );
}
export function hash(value: string) {
  return Array.from(value).reduce(
    (n, c) => (n * 31 + c.charCodeAt(0)) >>> 0,
    7,
  );
}
export function Artwork({
  episode,
  seed,
  label,
  large = false,
}: {
  episode?: AppEpisode;
  seed?: string;
  label?: string;
  large?: boolean;
}) {
  const { catalogue } = useApp();
  const [failed, setFailed] = useState<string[]>([]);
  const station = catalogue.stations.find(
    (s) => s.id === (episode?.station_id || seed),
  );
  const show = catalogue.shows.find((s) => s.id === seed);
  const source =
    episode?.artwork_url ||
    station?.artwork_url ||
    (show &&
      catalogue.stations.find((s) => s.id === show.station_id)?.artwork_url);
  const key = episode?.id || seed || "elsewhere";
  const n = hash(key);
  const id = useId().replace(/:/g, "");
  const palettes = [
    ["#e4b091", "#733e3e", "#f5dcc0"],
    ["#b9c2b0", "#263b35", "#d1d9b8"],
    ["#aebfce", "#2c4572", "#e0e3ed"],
    ["#dec858", "#6e532c", "#f6e2aa"],
    ["#c5accb", "#563d61", "#e4ced6"],
    ["#cba994", "#382d36", "#ebc0a6"],
  ];
  const p = palettes[n % palettes.length];
  const text = label || episode?.show_title || "Radio";
  if (source && /^(https?:\/\/|\/)/.test(source) && !failed.includes(source))
    return (
      <div
        className={"artwork broadcaster-art " + (large ? "artwork-large" : "")}
        aria-hidden="true"
      >
        <img
          src={source}
          alt=""
          loading={large ? "eager" : "lazy"}
          referrerPolicy="no-referrer"
          onError={() => setFailed((v) => [...v, source])}
        />
      </div>
    );
  return (
    <div
      className={"artwork " + (large ? "artwork-large" : "")}
      style={{ "--art-bg": p[0], "--art-ink": p[1] } as CSSProperties}
      aria-hidden="true"
    >
      <svg viewBox="0 0 500 500" role="presentation">
        <defs>
          <pattern id={id} width="6" height="6" patternUnits="userSpaceOnUse">
            <path d="M0 0v6" stroke={p[1]} opacity=".11" strokeWidth="1" />
          </pattern>
        </defs>
        <rect width="500" height="500" fill={p[0]} />
        {n % 3 === 0 ? (
          <>
            {Array.from({ length: 15 }, (_, i) => (
              <circle
                key={i}
                cx="265"
                cy="245"
                r={23 + i * 13}
                fill="none"
                stroke={p[1]}
                strokeWidth={i % 4 === 0 ? 9 : 2}
              />
            ))}
            <path d="M0 310 500 100v140L0 450Z" fill={p[2]} opacity=".8" />
          </>
        ) : n % 3 === 1 ? (
          <>
            {Array.from({ length: 10 }, (_, i) => (
              <path
                key={i}
                d={`M${-120 + i * 52} 500 Q${60 + i * 25} -240 ${220 + i * 45} 500`}
                stroke={p[1]}
                strokeWidth="21"
                fill="none"
              />
            ))}
            <circle cx="345" cy="150" r="70" fill={p[2]} />
          </>
        ) : (
          <>
            {Array.from({ length: 8 }, (_, i) => (
              <rect
                key={i}
                x={60 + i * 25}
                y={60 + i * 25}
                width={380 - i * 50}
                height={380 - i * 50}
                rx={n % 2 ? 160 : 1}
                fill="none"
                stroke={p[1]}
                strokeWidth="10"
              />
            ))}
            <circle cx="340" cy="160" r="83" fill={p[2]} opacity=".85" />
          </>
        )}
        <rect width="500" height="500" fill={`url(#${id})`} />
      </svg>
      <div className="art-label">
        <span>{episode?.station_name || "A listening collection"}</span>
        <strong dir="auto">{text}</strong>
        <span>
          ◌ &nbsp; {episode?.country || "ACROSS THE AIRWAVES"}{" "}
          <span>{String((n % 99) + 1).padStart(2, "0")}</span>
        </span>
      </div>
    </div>
  );
}
export function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const id = useId();
  useEffect(() => {
    const before = document.activeElement as HTMLElement;
    ref.current?.showModal();
    return () => {
      ref.current?.close();
      before?.focus();
    };
  }, []);
  return (
    <dialog
      ref={ref}
      className="modal"
      aria-labelledby={id}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          const r = e.currentTarget.getBoundingClientRect();
          if (
            e.clientX < r.left ||
            e.clientX > r.right ||
            e.clientY < r.top ||
            e.clientY > r.bottom
          )
            onClose();
        }
      }}
    >
      <div className="modal-head">
        <h2 id={id}>{title}</h2>
        <IconButton name="close" label="Close" onClick={onClose} />
      </div>
      {children}
    </dialog>
  );
}
export function Empty({ children }: { children: ReactNode }) {
  return (
    <div className="empty-state">
      <Icon name="headphones" size={32} />
      <p>{children}</p>
    </div>
  );
}
export function LanguageSelect() {
  const { lang, preference, t } = useApp();
  return (
    <label className="language-select">
      <Icon name="globe" size={16} />
      <span className="sr-only">{t("language")}</span>
      <select
        value={lang}
        onChange={(e) =>
          preference({ language: e.target.value as typeof lang })
        }
      >
        <option value="original">{t("original")}</option>
        <option value="en">English</option>
        <option value="fr">Français</option>
        <option value="ar">العربية</option>
        <option value="ja">日本語</option>
      </select>
    </label>
  );
}
